import { fetchBotConfig, buildConfig, buildConfigFromObject } from './config.js';
import { createWidget } from './widget.js';
import { DEFAULT_API_BASE_URL } from './constants.js';

// Captured synchronously at load time: document.currentScript is only
// meaningful while the embed <script> is executing.
const scriptEl = document.currentScript;

// Last programmatic mount options + resolved config, so update() can decide
// between an in-place refresh and a full re-mount (lang/theme changes).
let lastMountOptions = null;
let lastConfig = null;

function destroyCurrent() {
  if (window.__chatyxWidgetInstance && typeof window.__chatyxWidgetInstance.destroy === 'function') {
    try {
      window.__chatyxWidgetInstance.destroy();
    } catch (_) {
      /* noop */
    }
  }
  window.__chatyxWidgetInstance = null;
}

// Resolve a final config either from a plain object (programmatic) or from the
// script data attributes + the remote fetch (standard embed).
async function resolveConfig({ config, botId, apiBaseUrl }) {
  if (config) return buildConfigFromObject(config);
  let remoteConfig = {};
  if (botId) remoteConfig = await fetchBotConfig(botId, apiBaseUrl);
  return buildConfig(scriptEl, remoteConfig);
}

const ChatyxWidget = {
  // Standard auto-init path: read the embed <script> attributes, fetch the
  // remote config and mount the floating widget on document.body.
  async init() {
    const botId = scriptEl && scriptEl.dataset.botId;
    const apiBaseUrl = (scriptEl && scriptEl.dataset.apiBaseUrl) || DEFAULT_API_BASE_URL;
    destroyCurrent();
    const config = await resolveConfig({ botId, apiBaseUrl });
    lastMountOptions = null;
    lastConfig = config;
    window.__chatyxWidgetInstance = createWidget(config, apiBaseUrl, botId);
    return window.__chatyxWidgetInstance;
  },

  // Programmatic mount. Accepts a config object (preferred for the embedded
  // dashboard preview) or falls back to a remote fetch by botId. Supports the
  // embedded/inline mode options understood by createWidget.
  async mount(options = {}) {
    const apiBaseUrl = options.apiBaseUrl || (scriptEl && scriptEl.dataset.apiBaseUrl) || DEFAULT_API_BASE_URL;
    const botId = options.botId || (scriptEl && scriptEl.dataset.botId) || null;
    destroyCurrent();
    const config = await resolveConfig({ config: options.config, botId, apiBaseUrl });
    lastMountOptions = options;
    lastConfig = config;
    window.__chatyxWidgetInstance = createWidget(config, apiBaseUrl, botId, {
      container: options.container,
      embedded: options.embedded,
      alwaysOpen: options.alwaysOpen,
      ephemeral: options.ephemeral,
      skipConsent: options.skipConsent
    });
    return window.__chatyxWidgetInstance;
  },

  // Apply a new config to the mounted instance. Colour/text/branding/theme
  // changes are applied in place; only a language change requires a full
  // re-mount (the translated template is built once at creation time).
  update(configObject) {
    const instance = window.__chatyxWidgetInstance;
    if (!instance || typeof instance.update !== 'function') return null;

    const finalized = buildConfigFromObject(configObject);
    const langChanged = !lastConfig || finalized.lang !== lastConfig.lang;

    if (langChanged && lastMountOptions) {
      return this.mount({ ...lastMountOptions, config: configObject });
    }

    instance.update(finalized);
    lastConfig = finalized;
    return instance;
  },

  destroy() {
    destroyCurrent();
    lastMountOptions = null;
    lastConfig = null;
  }
};

window.ChatyxWidget = ChatyxWidget;

// Auto-init the standard embed unless explicitly opted out (e.g. the preview
// host page mounts the widget itself via ChatyxWidget.mount).
if (!(scriptEl && scriptEl.dataset.autoInit === 'false')) {
  ChatyxWidget.init();
}
